/**
 * connection-target.js — where to ask about one app's connection details.
 *
 * `GET /app-templates/installed/connection-info` is keyed by cluster +
 * namespace, with the workload and template narrowing the answer to THIS app.
 * Two callers need that quartet: the store action that loads the payload, and
 * the modal, which issues its own narrow `?reveal=1&keys=…` read when someone
 * copies a hidden credential. Deriving it twice is how the two drift apart, so
 * it is derived once, here.
 *
 * Pure: no imports, no DOM, no `@/` aliases. Covered by
 * `node --test tests/unit/conn-copy-reveal.test.mjs`.
 *
 * Author: Matew Makhatadze
 */

/**
 * Read the connection-info target off a UTS instance detail.
 *
 * Returns `null` when the cluster or namespace is missing rather than a
 * half-filled object: a request without them answers about the wrong thing,
 * and a caller that gets `null` can say so instead of asking.
 *
 * @param {object|null} instance a `userApps` instance detail
 * @returns {{cluster:string, namespace:string, workloadName:string,
 *            templateId:string}|null}
 */
export function connectionTargetOf(instance) {
  const inst = instance && typeof instance === 'object' ? instance : null
  if (!inst) return null
  const target = inst.target && typeof inst.target === 'object' ? inst.target : {}
  const cluster = target.cluster_name || ''
  const namespace = target.namespace || ''
  if (!cluster || !namespace) return null
  return {
    cluster,
    namespace,
    workloadName: inst.name || inst.workload_name || target.workload_name || '',
    templateId: inst.template_id || ''
  }
}
